// Web-side mirror of Packages/com.gamedate.flexocore/Runtime/Telemetry/FlexoTelemetry.cs.
// Single chokepoint for the AC 7.1 typed event trail.

export type FlexoEvent =
  | 'room_join_requested'
  | 'bridge_handshake_ok'
  | 'bridge_handshake_failed'
  | 'signalr_subscribed'
  | 'signalr_disconnected'
  | 'signalr_resumed'
  | 'local_spawn_attempted'
  | 'local_spawn_succeeded'
  | 'local_spawn_failed'
  | 'remote_roster_reconciled'
  | 'remote_player_added'
  | 'remote_player_removed'
  | 'position_update_drift_warning'
  | 'fake_room_fallback_triggered'
  // --- auth + funnel-correlation events (login-character-create-edit, US-15) ---
  | 'login_attempted'
  | 'login_succeeded'
  | 'login_rejected'             // payload: { reason: 'invalid' | 'banned' | 'suspended' }
  | 'session_refresh_attempted'  // ReconnectUser silent renew
  | 'session_refresh_succeeded'
  | 'session_refresh_failed'     // payload: { reason }
  | 'session_continuity_ok'      // editor opened/saved with a live token (US-5)
  | 'session_continuity_broken'
  | 'editor_state_restored'      // US-7 AC-1
  | 'funnel_first_session_completed' // US-6 AC-4 (completion = first successful save)
  // zone streaming (multi-room-world Phase 1) — kept symmetric with the C# enum
  | 'zone_loaded'
  | 'zone_unloaded'
  | 'zone_build_failed'
  // position persistence (multi-room-world Phase 2)
  | 'spawn_restored'
  // config-driven interactions (multi-room-world Phase 3)
  | 'trigger_fired'
  | 'puzzle_solved';

export interface FlexoTelemetryEvent {
  event: FlexoEvent;
  roomId: string | null;
  playerId: string | null;
  correlationId: string;
  ts: number;
  payload?: Record<string, unknown>;
}

const RING_BUFFER_SIZE = 1000;

declare global {
  interface Window {
    __flexoTelemetry__?: FlexoTelemetryEvent[];
  }
}

if (typeof window !== 'undefined' && !window.__flexoTelemetry__) {
  window.__flexoTelemetry__ = [];
}

// Events whose payload can carry moderation state (e.g. { reason: 'banned' }). We never write
// their payload to the console, even in dev, so moderation status can't be recovered from logs
// or a user-installed devtools recorder.
const SENSITIVE_EVENTS = new Set<FlexoEvent>([
  'login_rejected',
  'session_refresh_failed',
]);

// Console logging is dev-only. import.meta.env.DEV is statically replaced at build time, so prod
// bundles drop the console line entirely.
const CONSOLE_ENABLED =
  typeof import.meta !== 'undefined' && Boolean((import.meta as { env?: { DEV?: boolean } }).env?.DEV);

export function emit(e: FlexoTelemetryEvent): void {
  if (typeof window !== 'undefined') {
    const buf = window.__flexoTelemetry__ ?? [];
    buf.push(e);
    if (buf.length > RING_BUFFER_SIZE) buf.splice(0, buf.length - RING_BUFFER_SIZE);
    window.__flexoTelemetry__ = buf;
  }
  if (CONSOLE_ENABLED) {
    // eslint-disable-next-line no-console
    console.info('[FlexoTelemetry]', e.event, {
      cid: e.correlationId,
      roomId: e.roomId,
      playerId: e.playerId,
      payload: SENSITIVE_EVENTS.has(e.event) ? '[redacted]' : e.payload,
    });
  }
}

export function newCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
}

export function nowMs(): number {
  return Date.now();
}

/**
 * Forward a telemetry envelope received from Unity ("telemetry" message type)
 * into this sink. Unity emits the same `FlexoTelemetryEvent` shape under
 * `data.payload` of the bridge envelope.
 */
export function forwardFromUnity(unityPayload: unknown): void {
  if (!unityPayload || typeof unityPayload !== 'object') return;
  const candidate = unityPayload as Partial<FlexoTelemetryEvent>;
  if (!candidate.event) return;
  emit({
    event: candidate.event,
    roomId: candidate.roomId ?? null,
    playerId: candidate.playerId ?? null,
    correlationId: candidate.correlationId ?? '',
    ts: candidate.ts ?? nowMs(),
    payload: candidate.payload,
  });
}
